import { useCallback, useRef, useState } from "react";

export interface ISingleFlightSubmitHook {
    isSubmitting: boolean;
    runExclusive: (action: () => Promise<void>) => Promise<void>;
}

/**
 * Runs a submit action at most once at a time.
 *
 * The ref is the gate, not the state: two clicks dispatched in the same tick both enter the
 * handler before React re-renders the disabled button, so a state flag on its own still lets
 * the second one through. The state exists only so callers can disable their control.
 */
export function useSingleFlightSubmit(): ISingleFlightSubmitHook {
    const isRunningRef = useRef(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const runExclusive = useCallback(async (action: () => Promise<void>): Promise<void> => {
        if (isRunningRef.current) {
            return;
        }

        isRunningRef.current = true;
        setIsSubmitting(true);

        try {
            await action();
        } catch {
            // Callers invoke this from onClick, where a rejection becomes an unhandled
            // promise rejection. The hooks owning each request already report failures.
        } finally {
            isRunningRef.current = false;
            setIsSubmitting(false);
        }
    }, []);

    return { isSubmitting, runExclusive };
}
